import React, { useEffect, useState, useMemo } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import axios from 'axios';

interface LocationOption {
  place_id: number;
  display_name: string;
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string, field: string) => void;
  error?: string;
  maxLength?: number;
  label?: string;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  error,
  maxLength = 275,
  label = 'Location',
}) => {
  const [inputValue, setInputValue] = useState<string>(value);
  const [options, setOptions] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);


  const fetchLocations = useMemo(
    () => async (query: string) => {
      setLoading(true);
      try {
        const response = await axios.get<LocationOption[]>(
          'https://nominatim.openstreetmap.org/search',
          {
            params: {
              q: query,
              format: 'json',
              addressdetails: 1,
              limit: 5,
            },
          }
        );
        setOptions(response.data);
      } catch (error) {
        console.error('Error fetching location suggestions:', error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (inputValue.trim().length < 2) {
      setOptions([]); // 🔁 Clear suggestions
      return;
    }

    const timeout = setTimeout(() => {
      fetchLocations(inputValue);
    }, 500); // debounce 500ms

    return () => clearTimeout(timeout);
  }, [inputValue, fetchLocations]);

  const computedOptions = React.useMemo(() => {
    // If inputValue is empty or already present, just return options
    if (
      !inputValue.trim() ||
      options.some(opt => opt.display_name.toLowerCase() === inputValue.trim().toLowerCase())
    ) {
      return options;
    }
    // Add manual input as first option
    return [
      { place_id: -1, display_name: inputValue.trim() },
      ...options,
    ];
  }, [options, inputValue]);

  return (
    <Autocomplete
      fullWidth
      freeSolo
      options={computedOptions}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.display_name
      }
      filterOptions={(x) => x} // show raw server data
      value={value}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
        onChange(newInputValue, 'location'); // <-- Add this line
      }}
      onChange={(_, newValue) => {
        if (typeof newValue === 'string') {
          onChange(newValue, 'location');
        } else if (newValue) {
          onChange(newValue.display_name, 'location');
        }
      }}
      loading={loading}
      sx={{ width: '62.3vw' }}
      renderInput={(params) => (
        <TextField
          required
          {...params}
          label={label}
          variant="outlined"
          size="small"
          error={!!error}
          helperText={error}
          inputProps={{
            ...params.inputProps,
            maxLength,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}

    />
  );
};

export default LocationAutocomplete;
